/**
 * AccountPage — Mon compte self-service.
 *
 * Refonte R5 (mai 2026) :
 *   - Header glass cohérent DashboardPage (LogoMark Landmark + bouton Retour + ThemeToggle)
 *   - Card identité avec Avatar grand + Badge role
 *   - 5 sections en Card glass : Profil / Sécurité / Préférences / Communication / RGPD
 *
 * Logique métier 100% préservée :
 *   - useAuth : user, tenant, updateMyProfile, changeMyPassword, exportMyData,
 *     deleteMyAccount, logoutAllSessions
 *   - ProfileSection : updateMyProfile (prenom, nom, telephone)
 *   - SecuritySection : 2FA status + lien /2fa, changeMyPassword form, logoutAllSessions + redirect
 *   - PreferencesSection : thème (updateMyProfile) + canaux d'habitant (api.me, lot N4)
 *   - CommunicationMunicipaleSection : apiL15.grc.setNewsletterOptIn + push PWA subscribe/unsubscribe
 *   - RgpdSection : exportMyData + deleteMyAccount (admin non supprimables, confirm flow)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Input,
  Field,
  Badge,
  Alert,
  LogoMark,
  Avatar,
  SectionEyebrow,
} from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/lib/auth-context';
import { api as apiL15, ApiException } from '@/lib/api';
import {
  isPushSupported,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isCurrentlySubscribed,
  isServerPushAvailable,                   // R13.5 — détection VAPID backend
} from '@/lib/push-subscription';
import {
  ArrowLeft,
  Landmark,
  Save,
  KeyRound,
  Shield,
  LogOut,
  Download,
  Trash2,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Mail as MailIcon,
  Smartphone as SmartphoneIcon,
  MessageSquare as SmsIcon,
} from 'lucide-react';
import type { ThemePreference } from '@roronoa-collectivites/shared-types';

export function AccountPage() {
  const navigate = useNavigate();
  const {
    user,
    tenant,
    updateMyProfile,
    changeMyPassword,
    exportMyData,
    deleteMyAccount,
    logoutAllSessions,
  } = useAuth();

  if (!user || !tenant) return null;

  const userInitials = user.prenom?.[0]?.toUpperCase() ?? user.nom?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="min-h-screen text-ink">
      {/* ===== Topbar glass (cohérent DashboardPage R4) ===== */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 border-b border-border px-4 py-3 backdrop-blur-glass sm:px-6"
        style={{
          backgroundImage:
            'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
            'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
        }}
      >
        <LogoMark icon={Landmark} accent="tech" size="md" aria-label="VPC" />
        <div className="min-w-0">
          <SectionEyebrow>Espace personnel</SectionEyebrow>
          <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
            Mon compte
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Tableau de bord</span>
          </Button>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 animate-fade-in space-y-5">
        {/* ===== En-tête identité ===== */}
        <Card variant="glass" padded>
          <div className="flex items-center gap-4">
            <Avatar initials={userInitials} accent="tech" size="lg" />
            <div className="min-w-0">
              <SectionEyebrow as="div">{tenant.nom}</SectionEyebrow>
              <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink">
                {user.prenom} {user.nom}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="tech" size="sm">{user.role}</Badge>
                <span className="text-ink-subtle">·</span>
                <span className="text-ink-muted truncate">{user.email}</span>
              </div>
            </div>
          </div>
        </Card>

        <ProfileSection user={user} updateMyProfile={updateMyProfile} />
        <EmailChangeSection currentEmail={user.email} />
        <SecuritySection
          user={user}
          changeMyPassword={changeMyPassword}
          logoutAllSessions={logoutAllSessions}
        />
        <PreferencesSection user={user} updateMyProfile={updateMyProfile} />
        <CommunicationMunicipaleSection />
        <RgpdSection
          user={user}
          exportMyData={exportMyData}
          deleteMyAccount={deleteMyAccount}
        />
      </main>
    </div>
  );
}

// ============================================================================
// Section 1 — Profil (prénom / nom / téléphone)
// ============================================================================

function ProfileSection({
  user,
  updateMyProfile,
}: {
  user: { prenom: string; nom: string; telephone: string | null };
  updateMyProfile: (input: { prenom?: string; nom?: string; telephone?: string | null }) => Promise<void>;
}) {
  const [prenom, setPrenom] = useState(user.prenom);
  const [nom, setNom] = useState(user.nom);
  const [telephone, setTelephone] = useState(user.telephone ?? '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateMyProfile({
        prenom: prenom.trim(),
        nom: nom.trim(),
        telephone: telephone.trim() === '' ? null : telephone.trim(),
      });
      setSuccess('Profil enregistré.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card variant="glass" padded>
      <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">Profil</h2>
      <form onSubmit={onSubmit} className="mt-4 space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {success && <Alert variant="success" icon={CheckCircle2}>{success}</Alert>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Prénom" htmlFor="p-prenom" required>
            <Input id="p-prenom" value={prenom} onChange={(e) => setPrenom(e.target.value)} required />
          </Field>
          <Field label="Nom" htmlFor="p-nom" required>
            <Input id="p-nom" value={nom} onChange={(e) => setNom(e.target.value)} required />
          </Field>
          <Field label="Téléphone" htmlFor="p-tel" hint="Format libre. Optionnel.">
            <Input
              id="p-tel"
              type="tel"
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
            />
          </Field>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" variant="primary" disabled={saving}>
            <Save className="h-4 w-4" aria-hidden="true" /> Enregistrer
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================================
// Section 1b — Changement d'adresse email (vérifié, anti-enfermement)
// ============================================================================
function EmailChangeSection({ currentEmail }: { currentEmail: string }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await apiL15.auth.requestEmailChange({ newEmail: newEmail.trim(), currentPassword: password });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Demande impossible');
    } finally { setBusy(false); }
  }

  return (
    <Card variant="glass" padded>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-grc-tint/70 text-grc-fg" aria-hidden="true">
          <MailIcon className="h-4 w-4" />
        </div>
        <div>
          <h2 className="font-display text-base font-semibold text-ink">Adresse email</h2>
          <p className="text-2xs text-ink-subtle">Identifiant de connexion — le changement est confirmé par un lien envoyé à la nouvelle adresse.</p>
        </div>
      </div>

      <div className="text-sm text-ink-muted">Adresse actuelle : <span className="font-medium text-ink">{currentEmail}</span></div>

      {sent ? (
        <Alert variant="success" icon={CheckCircle2} className="mt-3">
          Un lien de confirmation a été envoyé à <strong>{newEmail}</strong>. Votre adresse actuelle reste active
          tant que vous n'avez pas cliqué sur ce lien (valable 2 heures).
        </Alert>
      ) : !open ? (
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>Changer mon adresse email</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
          <Field label="Nouvelle adresse email" htmlFor="ec-new" required>
            <Input id="ec-new" type="email" autoComplete="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required placeholder="nouvelle@adresse.fr" />
          </Field>
          <Field label="Mot de passe actuel" htmlFor="ec-pwd" required hint="Pour sécuriser le changement d'identifiant.">
            <Input id="ec-pwd" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => { setOpen(false); setError(null); }} disabled={busy}>Annuler</Button>
            <Button type="submit" variant="primary" disabled={busy || !newEmail.trim() || !password}>{busy ? 'Envoi…' : 'Envoyer le lien de confirmation'}</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

// ============================================================================
// Section 2 — Sécurité (2FA + changer mdp + logout all)
// ============================================================================

function SecuritySection({
  user,
  changeMyPassword,
  logoutAllSessions,
}: {
  user: { twoFactorEnabled: boolean };
  changeMyPassword: (current: string, next: string) => Promise<void>;
  logoutAllSessions: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);

  async function onChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      setError('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await changeMyPassword(current, next);
      setSuccess('Mot de passe modifié. Un email de confirmation t\'a été envoyé.');
      setCurrent('');
      setNext('');
      setConfirm('');
      setTimeout(() => setSuccess(null), 6000);
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  }

  async function onLogoutAll() {
    try {
      await logoutAllSessions();
      navigate('/connexion', { replace: true });
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    }
  }

  return (
    <Card variant="glass" padded>
      <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">Sécurité</h2>

      {/* 2FA status */}
      <div className="mt-4 flex items-center justify-between gap-3 rounded-r-lg border border-border bg-surface/40 px-4 py-3 backdrop-blur-glass">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-gradient-tech text-white flex-shrink-0"
            style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-tech) / 0.45), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
            aria-hidden="true"
          >
            <Shield className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">Double authentification (2FA)</div>
            <div className="text-xs text-ink-muted">
              {user.twoFactorEnabled
                ? 'Activée — un code à 6 chiffres est demandé à chaque connexion.'
                : 'Désactivée — recommandé pour les comptes admin et manager.'}
            </div>
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={() => navigate('/2fa')}>
          {user.twoFactorEnabled ? 'Gérer' : 'Activer'}
        </Button>
      </div>

      {/* Change password */}
      <form onSubmit={onChangePassword} className="mt-5 space-y-3">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {success && <Alert variant="success" icon={CheckCircle2}>{success}</Alert>}

        <h3 className="flex items-center gap-2 text-sm font-medium text-ink">
          <KeyRound className="h-4 w-4" aria-hidden="true" /> Changer mon mot de passe
        </h3>
        <Field label="Mot de passe actuel" htmlFor="cp-current" required>
          <Input
            id="cp-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </Field>
        <Field
          label="Nouveau mot de passe"
          htmlFor="cp-next"
          hint="Minimum 12 caractères. Évite les mots de ton profil (nom, email)."
          required
        >
          <Input
            id="cp-next"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
        </Field>
        <Field label="Confirme le nouveau mot de passe" htmlFor="cp-confirm" required>
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </Field>
        <div className="flex justify-end pt-1">
          <Button type="submit" variant="primary" disabled={saving || !current || !next || !confirm}>
            <KeyRound className="h-4 w-4" aria-hidden="true" /> Modifier
          </Button>
        </div>
      </form>

      {/* Logout all */}
      <div className="mt-6 border-t border-border pt-4">
        <h3 className="text-sm font-medium text-ink">Sessions actives</h3>
        <p className="mt-1 text-xs text-ink-muted">
          Si tu suspectes qu'un autre appareil utilise ton compte, déconnecte toutes les sessions.
          Tu seras renvoyé sur la page de connexion.
        </p>
        {confirmLogoutAll ? (
          <Alert variant="warning" icon={AlertTriangle} className="mt-3">
            <p className="font-medium">Déconnecter toutes les sessions&nbsp;?</p>
            <div className="mt-3 flex gap-2">
              <Button variant="primary" size="sm" onClick={onLogoutAll}>
                Oui, déconnecter toutes
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmLogoutAll(false)}>
                Annuler
              </Button>
            </div>
          </Alert>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setConfirmLogoutAll(true)} className="mt-2">
            <LogOut className="h-4 w-4" aria-hidden="true" /> Déconnecter toutes les sessions
          </Button>
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// Section 3 — Préférences (thème + notifications d'habitant)
// ============================================================================

function PreferencesSection({
  user,
  updateMyProfile,
}: {
  user: { themePreference: ThemePreference };
  updateMyProfile: (input: { themePreference?: ThemePreference }) => Promise<void>;
}) {
  const [theme, setTheme] = useState<ThemePreference>(user.themePreference);
  const [prefs, setPrefs] = useState<{ email: boolean; push: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const p = await apiL15.me.getPreferencesNotifications();
        setPrefs({ email: p.email, push: p.push });
      } catch {
        setPrefs({ email: true, push: true }); // best-effort : l'écran reste utilisable
      }
    })();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Deux réglages de nature différente, deux endpoints :
      //   · le thème est un attribut du compte ;
      //   · les canaux de notification sont une préférence D'HABITANT (lot N4),
      //     séparée depuis qu'on a corrigé le bug des deux casquettes.
      await updateMyProfile({ themePreference: theme });
      if (prefs) await apiL15.me.setPreferencesNotifications(prefs);
      setSuccess('Préférences enregistrées.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card variant="glass" padded>
      <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">Préférences</h2>
      <form onSubmit={onSubmit} className="mt-4 space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {success && <Alert variant="success" icon={CheckCircle2}>{success}</Alert>}

        <Field label="Thème" htmlFor="pref-theme">
          <select
            id="pref-theme"
            value={theme}
            onChange={(e) => setTheme(e.target.value as ThemePreference)}
            className="h-11 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg focus:border-tech hover:border-border-strong"
          >
            <option value="light">Clair</option>
            <option value="dark">Sombre</option>
            <option value="system">Système</option>
          </select>
        </Field>

        {/*
          ⚠️ LE LIBELLÉ COMPTE — c'est la moitié de la correction du lot N4.
          Avant, cette case disait « Recevoir les notifications par email », tout
          court. Un agent qui habite la commune croyait donc couper TOUTES ses
          notifications : le back-end faisait d'ailleurs exactement ça (le bug).
          Le back-end est corrigé, mais sans dire explicitement « d'habitant »,
          on garderait le malentendu à l'écran.
        */}
        {prefs && (
          <div className="rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-3.5">
            <p className="text-sm font-medium text-ink">Mes notifications d'habitant</p>
            <p className="mt-1 text-xs text-ink-muted">
              Comment souhaitez-vous être prévenu des informations que la mairie vous
              adresse en tant qu'habitant (alertes, rendez-vous, dossiers…) ?
            </p>

            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-sm text-ink cursor-not-allowed opacity-70">
                <input type="checkbox" checked disabled
                  className="h-4 w-4 rounded border-border bg-surface text-tech" />
                Dans l'application (toujours actif)
              </label>
              <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={prefs.email}
                  onChange={(e) => setPrefs({ ...prefs, email: e.target.checked })}
                  className="h-4 w-4 rounded border-border bg-surface text-tech focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg cursor-pointer"
                />
                Par e-mail
              </label>
              <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={prefs.push}
                  onChange={(e) => setPrefs({ ...prefs, push: e.target.checked })}
                  className="h-4 w-4 rounded border-border bg-surface text-tech focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg cursor-pointer"
                />
                Sur mon téléphone
              </label>
            </div>

            <p className="mt-3 text-xs text-ink-muted">
              Ces réglages ne concernent <strong>que</strong> les communications qui vous
              sont adressées en tant qu'habitant. Si vous travaillez pour la commune,
              vos notifications de travail ne sont pas affectées.
            </p>
            <p className="mt-1.5 text-xs text-ink-muted">
              Les alertes de sécurité (changement de mot de passe, session hotline
              One For All Distribution…) restent envoyées dans tous les cas.
            </p>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button type="submit" variant="primary" disabled={saving}>
            <Save className="h-4 w-4" aria-hidden="true" /> Enregistrer
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================================
// Section 4 — Communication municipale (newsletter + push PWA)
// ============================================================================

function CommunicationMunicipaleSection() {
  const { user } = useAuth();                                      // R13.3 — pour lire newsletterOptIn initial
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState<'newsletter' | 'push' | 'sms' | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Opt-in SMS d'alerte (mig. 141) : utilise le numéro du PROFIL (une seule
  // source, pas de champ séparé → pas de doublon). Statut chargé depuis le serveur.
  const [smsAbonne, setSmsAbonne] = useState(false);
  const [smsTelephone, setSmsTelephone] = useState<string | null>(null);
  const [smsLoaded, setSmsLoaded] = useState(false);
  const [smsSecteurs, setSmsSecteurs] = useState<Array<{ id: string; libelle: string }>>([]);
  const [smsSecteurIds, setSmsSecteurIds] = useState<string[]>([]);
  const [smsCartes, setSmsCartes] = useState<Array<{ id: string; libelle: string; typeFichier: 'pdf' | 'image'; url: string }>>([]);
  // R13.3 — Initialise à partir du backend GET /me (newsletterOptIn ajouté à toUserDTO)
  const [newsletterEnabled, setNewsletterEnabled] = useState<boolean | null>(
    (user as { newsletterOptIn?: boolean } | null)?.newsletterOptIn ?? null
  );
  // R13.5 — Détecte si le serveur a VAPID configuré. Si non, on désactive le toggle push
  // pour ne PAS déclencher Notification.requestPermission() (qui ouvrirait une modale browser).
  const [serverPushAvailable, setServerPushAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    // Sync si user change (logout/login)
    const initial = (user as { newsletterOptIn?: boolean } | null)?.newsletterOptIn ?? false;
    setNewsletterEnabled(initial);
    void isCurrentlySubscribed().then(setPushEnabled);
    // R13.5 — Détection VAPID backend (au mount + à chaque user change)
    void isServerPushAvailable().then(setServerPushAvailable);
    // Statut d'abonnement SMS réel (basé sur le téléphone du profil)
    void apiL15.grc.sms.monAbonnement()
      .then((r) => { setSmsAbonne(r.abonne); setSmsTelephone(r.telephone); setSmsSecteurIds(r.secteurIds ?? []); })
      .catch(() => { setSmsAbonne(false); setSmsTelephone(null); })
      .finally(() => setSmsLoaded(true));
    void apiL15.grc.sms.secteurs.list()
      .then((r) => setSmsSecteurs(r.secteurs.map((s) => ({ id: s.id, libelle: s.libelle }))))
      .catch(() => setSmsSecteurs([]));
    void apiL15.grc.sms.secteurs.cartes.list()
      .then((r) => setSmsCartes(r.cartes.map((c) => ({ id: c.id, libelle: c.libelle, typeFichier: c.typeFichier, url: c.url }))))
      .catch(() => setSmsCartes([]));
  }, [user]);

  async function toggleNewsletter(next: boolean) {
    setBusy('newsletter'); setError(null); setSuccess(null);
    try {
      await apiL15.grc.setNewsletterOptIn(next);
      setNewsletterEnabled(next);
      setSuccess(next ? 'Abonné·e à la newsletter municipale' : 'Désabonné·e');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    } finally {
      setBusy(null);
    }
  }

  async function togglePush(next: boolean) {
    setBusy('push'); setError(null); setSuccess(null);
    try {
      if (next) {
        await subscribeToPushNotifications();
        setPushEnabled(true);
        setSuccess('Notifications push activées');
      } else {
        await unsubscribeFromPushNotifications();
        setPushEnabled(false);
        setSuccess('Notifications push désactivées');
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur push');
    } finally {
      setBusy(null);
    }
  }

  async function toggleSms(next: boolean) {
    setBusy('sms'); setError(null); setSuccess(null);
    // Numéro = celui du profil (une seule source de vérité).
    const telProfil = (user as { telephone?: string | null } | null)?.telephone ?? smsTelephone;
    try {
      if (next) {
        if (!telProfil || !telProfil.trim()) {
          setError('Renseignez d’abord votre numéro de mobile dans « Profil » ci-dessus, puis enregistrez.');
          setBusy(null);
          return;
        }
        await apiL15.grc.sms.abonner({ telephone: telProfil.trim(), secteurIds: smsSecteurIds });
        setSmsAbonne(true);
        setSuccess('Abonné·e aux alertes SMS');
      } else {
        if (telProfil) await apiL15.grc.sms.desabonner(telProfil.trim());
        setSmsAbonne(false);
        setSuccess('Désabonné·e des alertes SMS');
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    } finally {
      setBusy(null);
    }
  }

  const pushSupported = isPushSupported();

  // Met à jour les secteurs choisis (ré-abonne avec le même numéro + nouveaux secteurs).
  async function majSecteursCitoyen(nextIds: string[]) {
    setSmsSecteurIds(nextIds);
    const telProfil = (user as { telephone?: string | null } | null)?.telephone ?? smsTelephone;
    if (!smsAbonne || !telProfil) return; // n'enregistre que si déjà abonné
    setBusy('sms'); setError(null);
    try {
      await apiL15.grc.sms.abonner({ telephone: telProfil.trim(), secteurIds: nextIds });
      setSuccess('Secteurs mis à jour');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    } finally {
      setBusy(null);
    }
  }
  // RGAA/UX — sur desktop, le push PWA est peu pertinent (pas d'app installée).
  // On détecte l'absence d'écran tactile + fenêtre large pour adapter le message.
  const isDesktop =
    typeof window !== 'undefined' &&
    !('ontouchstart' in window) &&
    (navigator.maxTouchPoints ?? 0) === 0 &&
    window.matchMedia('(min-width: 1024px)').matches;

  return (
    <Card variant="glass" padded>
      <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">Communication municipale</h2>
      <div className="mt-4 space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {success && <Alert variant="success" icon={CheckCircle2}>{success}</Alert>}

        {/* Newsletter */}
        <div className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-3.5">
          <div
            className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-gradient-grc text-white flex-shrink-0"
            style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-grc) / 0.45), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
            aria-hidden="true"
          >
            <MailIcon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="text-sm font-medium text-ink">Newsletter municipale</span>
              <input
                type="checkbox"
                checked={newsletterEnabled ?? false}
                disabled={busy === 'newsletter' || newsletterEnabled === null}
                onChange={(e) => void toggleNewsletter(e.target.checked)}
                className="h-5 w-5 rounded border-border bg-surface text-grc focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              />
            </label>
            <p className="mt-1 text-xs text-ink-muted">
              Recevez par email les actualités de la mairie et les alertes municipales
              (information, avis). Désabonnement à tout moment.
            </p>
          </div>
        </div>

        {/* Push PWA */}
        <div className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-3.5">
          <div
            className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-gradient-grc text-white flex-shrink-0"
            style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-grc) / 0.45), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
            aria-hidden="true"
          >
            <SmartphoneIcon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="text-sm font-medium text-ink">Notifications push (navigateur)</span>
              <input
                type="checkbox"
                checked={pushEnabled ?? false}
                /* R13.5 — disabled si VAPID pas configuré côté serveur : évite le clic qui
                   déclencherait Notification.requestPermission() (modale browser native) */
                disabled={
                  busy === 'push' ||
                  pushEnabled === null ||
                  !pushSupported ||
                  serverPushAvailable === false ||
                  (isDesktop && !pushEnabled)
                }
                onChange={(e) => void togglePush(e.target.checked)}
                className="h-5 w-5 rounded border-border bg-surface text-grc focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              />
            </label>
            {!pushSupported ? (
              <p className="mt-1 text-xs text-warning">
                Votre navigateur ne supporte pas les notifications push.
                Essayez Chrome, Firefox ou Edge récent.
              </p>
            ) : serverPushAvailable === false ? (
              <p className="mt-1 text-xs text-warning">
                Notifications push pas encore disponibles sur ce serveur. Cette fonctionnalité
                sera activée par votre commune prochainement.
              </p>
            ) : isDesktop && !pushEnabled ? (
              <p className="mt-1 text-xs text-ink-muted">
                Les notifications push sont surtout utiles sur mobile. Pour les recevoir,
                installez l’application sur votre téléphone et activez-les depuis votre compte.
                Sur ordinateur, préférez la newsletter ou les alertes SMS ci-dessus.
              </p>
            ) : (
              <p className="mt-1 text-xs text-ink-muted">
                Recevez les <strong>avis</strong>, <strong>alertes</strong> et <strong>crises</strong>{' '}
                en notification navigateur, même quand l'onglet est fermé.
                Nécessite la permission Notification de votre navigateur.
              </p>
            )}
          </div>
        </div>

        {/* Alertes SMS (opt-in) */}
        <div className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-3.5">
          <div
            className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-gradient-grc text-white flex-shrink-0"
            style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-grc) / 0.45), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
            aria-hidden="true"
          >
            <SmsIcon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="text-sm font-medium text-ink">Alertes SMS</span>
              <input
                type="checkbox"
                checked={smsAbonne}
                disabled={busy === 'sms' || !smsLoaded}
                onChange={(e) => void toggleSms(e.target.checked)}
                className="h-5 w-5 rounded border-border bg-surface text-grc focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              />
            </label>
            <p className="mt-1 text-xs text-ink-muted">
              Recevez les alertes municipales par SMS (avis et alertes) sur le numéro de votre
              profil{smsTelephone ? <> (<strong>{smsTelephone}</strong>)</> : null}. Utile si vous
              n’avez pas l’application ni les notifications. Répondez STOP à tout moment pour
              vous désabonner. <em>Enregistré automatiquement au clic.</em>
            </p>
            {!((user as { telephone?: string | null } | null)?.telephone) && (
              <p className="mt-1 text-xs text-warning">
                Aucun numéro dans votre profil : renseignez-le dans « Profil » ci-dessus et
                enregistrez, puis cochez cette case.
              </p>
            )}
            {smsAbonne && smsSecteurs.length > 0 && (
              <div className="mt-3">
                <p className="mb-1.5 text-xs font-medium text-ink">Mes secteurs</p>
                <p className="mb-2 text-[11px] text-ink-subtle">
                  Choisissez les zones qui vous concernent pour recevoir les alertes ciblées.
                  Sans sélection, vous recevez uniquement les alertes envoyées à toute la commune.
                </p>
                {smsCartes.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {smsCartes.map((c) => (
                      c.typeFichier === 'image' && c.url ? (
                        <a key={c.id} href={c.url} target="_blank" rel="noreferrer" className="block">
                          <img src={c.url} alt={c.libelle} className="max-h-48 w-full rounded border border-border object-contain" />
                          <span className="mt-0.5 block text-[11px] text-ink-subtle">{c.libelle}</span>
                        </a>
                      ) : (
                        <a key={c.id} href={c.url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-grc hover:underline">
                          Voir le plan des secteurs : {c.libelle} (PDF)
                        </a>
                      )
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {smsSecteurs.map((s) => {
                    const on = smsSecteurIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        disabled={busy === 'sms'}
                        onClick={() => majSecteursCitoyen(on ? smsSecteurIds.filter((x) => x !== s.id) : [...smsSecteurIds, s.id])}
                        className={`rounded-full border px-3 py-1 text-xs transition disabled:opacity-50 ${on ? 'border-grc bg-grc/10 text-grc font-medium' : 'border-border text-ink-muted hover:border-grc/50'}`}
                        aria-pressed={on}
                      >
                        {s.libelle}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-[11px] text-ink-subtle">
          <strong>Pour les crises</strong> (catastrophe naturelle, alerte sanitaire), la mairie
          peut activer en plus le panneau lumineux et le PCS — vous serez prévenu par tous
          les canaux, indépendamment de vos préférences ici.
        </p>
      </div>
    </Card>
  );
}

// ============================================================================
// Section 5 — RGPD (export + suppression)
// ============================================================================

function RgpdSection({
  user,
  exportMyData,
  deleteMyAccount,
}: {
  user: { role: string };
  exportMyData: () => Promise<void>;
  deleteMyAccount: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user.role === 'super_admin' || user.role === 'admin';

  async function onExport() {
    setExporting(true);
    setError(null);
    try {
      await exportMyData();
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    } finally {
      setExporting(false);
    }
  }

  async function onDelete() {
    setError(null);
    try {
      await deleteMyAccount();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur') : 'Erreur réseau');
    }
  }

  return (
    <Card variant="glass" padded>
      <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
        Données personnelles (RGPD)
      </h2>
      <div className="mt-4 space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

        {/* Export — droit d'accès art. 15 */}
        <div className="rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-4">
          <h3 className="flex items-center gap-2 text-sm font-medium text-ink">
            <Download className="h-4 w-4" aria-hidden="true" />
            Exporter mes données (droit d'accès — RGPD art. 15)
          </h3>
          <p className="mt-1 text-xs text-ink-muted">
            Télécharge un fichier JSON contenant toutes les informations associées à ton compte :
            profil, historique d'audit, signalements créés, etc.
          </p>
          <Button variant="secondary" size="sm" onClick={onExport} disabled={exporting} className="mt-3">
            <Download className="h-4 w-4" aria-hidden="true" />
            {exporting ? 'Préparation…' : 'Télécharger l\'export JSON'}
          </Button>
        </div>

        {/* Suppression — droit à l'oubli art. 17 */}
        <div className="rounded-r-lg border border-urgent/30 bg-surface/30 backdrop-blur-glass p-4">
          <h3 className="flex items-center gap-2 text-sm font-medium text-urgent">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Supprimer mon compte (droit à l'oubli — RGPD art. 17)
          </h3>
          <p className="mt-1 text-xs text-ink-muted">
            Cette action anonymise toutes tes données personnelles (email, prénom, nom, téléphone,
            2FA). L'identifiant interne est conservé pour préserver l'intégrité des données métier
            (signalements, audit log). <strong>Irréversible</strong>.
          </p>
          {isAdmin ? (
            <Alert variant="info" icon={AlertCircle} className="mt-3">
              Les comptes <strong>admin</strong> ne peuvent pas être supprimés en self-service.
              Contacte un autre admin de ta collectivité pour faire transférer ton rôle d'abord.
            </Alert>
          ) : confirmDelete ? (
            <Alert variant="warning" icon={AlertTriangle} className="mt-3">
              <p className="font-medium">Confirmer la suppression définitive&nbsp;?</p>
              <p className="mt-1 text-sm">
                Tu seras déconnecté immédiatement. Aucune récupération possible.
              </p>
              <div className="mt-3 flex gap-2">
                <Button variant="danger" size="sm" onClick={onDelete}>
                  Oui, supprimer mon compte
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                  Annuler
                </Button>
              </div>
            </Alert>
          ) : (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} className="mt-3">
              <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer mon compte
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
